import { getAuthenticatedSupabaseClient } from '@/integrations/supabase/client';
import type { ProductCategory } from '@/components/floor-plans/FloorPlanViewerV2/components/CategoryDropdown';
import { logger } from '@/lib/logger';
import { requireAuthenticatedContext } from '@/lib/auth';

// Export type needed by PortalImportDialog
export interface ImportableProduct {
  portalItemId: number;
  name: string;
  brand?: string;
  model?: string;
  description?: string;
  partNumber?: string;
  quantity: number;
  unitPrice: number;
  sellPrice: number;
  referenceId?: string;
  areaId: number;
  areaName: string;
  optionId: number;
  optionName: string;
  imageUrl?: string;
  specifications?: any;
}

interface PortalProduct {
  id: string;
  name: string;
  description?: string;
  brand?: string;
  model?: string;
  partNumber?: string;
  quantity: number;
  unitPrice: number;
  sellPrice: number;
  totalPrice: number;
  referenceId?: string;
  area?: string;
  option?: string;
  imageUrl?: string;
  specifications?: any;
}

// CategorySelection type matching the UI component
export interface CategorySelection {
  category: string;
  subcategory?: string;
}

interface ProductSelection {
  productId: string;
  category: CategorySelection | string | null;
  subcategory?: string | null; // Legacy field for backwards compatibility
  referenceId?: string;
}

interface ImportResult {
  success: boolean;
  importedCount: number;
  libraryProductIds: string[];
  errors?: string[];
  productsCreated: number;
  productsUpdated: number;
  itemsTracked: number;
}

interface ProductGroup {
  referenceId: string;
  products: PortalProduct[];
  category: ProductCategory | null;
  subcategory?: string | null;
}

export class PortalImportService {
  private organizationId: string;
  private floorPlanId?: string;
  private userId?: string;

  constructor(organizationId: string, floorPlanId?: string, userId?: string) {
    this.organizationId = organizationId;
    this.floorPlanId = floorPlanId;
    this.userId = userId;
  }

  private async getClient() {
    return getAuthenticatedSupabaseClient();
  }

  /**
   * Import selected products with proper deduplication and tracking
   * Production-ready implementation with transactions and error handling
   */
  async importProducts(
    products: PortalProduct[],
    selections: ProductSelection[],
    proposalId: number,
    proposalName: string,
  ): Promise<ImportResult> {
    const errors: string[] = [];
    const libraryProductIds = new Set<string>();
    let productsCreated = 0;
    let productsUpdated = 0;
    let itemsTracked = 0;

    try {
      // Validate user context
      if (!this.userId) {
        logger.error('User ID not provided to import service', {
          organizationId: this.organizationId,
          floorPlanId: this.floorPlanId,
        });
        throw new Error('User authentication context missing - please refresh and try again');
      }

      logger.debug('Import started', {
        userId: this.userId,
        organizationId: this.organizationId,
        floorPlanId: this.floorPlanId,
        productCount: products.length,
        selectionCount: selections.length,
      });

      // Create selection map for easy lookup
      const selectionMap = new Map(selections.map(s => [s.productId, s]));

      // Group products by referenceId for deduplication
      const productGroups = this.groupProductsByReferenceId(products, selectionMap);

      logger.debug(
        `Processing ${productGroups.length} unique products from ${products.length} Portal items`,
      );

      // Process each unique product group
      for (const group of productGroups) {
        try {
          // Step 1: Create or update ONE product_library entry per referenceId
          const libraryProduct = await this.createOrUpdateLibraryProduct(
            group.products[0], // Use first product as template
            group.category,
            group.subcategory,
          );

          if (!libraryProduct) {
            throw new Error(`Failed to create/update product for referenceId ${group.referenceId}`);
          }

          libraryProductIds.add(libraryProduct.id);

          if (libraryProduct.created) {
            productsCreated++;
          } else {
            productsUpdated++;
          }

          // Step 2: Track ALL Portal items that share this referenceId
          for (const product of group.products) {
            try {
              await this.createImportedProductEntry(
                product,
                libraryProduct.id,
                proposalId,
                proposalName,
                group.category,
              );
              itemsTracked++;
            } catch (trackError: any) {
              logger.error(`Error tracking Portal item ${product.id}:`, trackError);
              errors.push(`Failed to track item ${product.name}: ${trackError.message}`);
            }
          }
        } catch (error: any) {
          logger.error(`Error processing product group ${group.referenceId}:`, error);
          errors.push(`Failed to import ${group.products[0].name}: ${error.message}`);
        }
      }

      const result: ImportResult = {
        success: libraryProductIds.size > 0,
        importedCount: libraryProductIds.size,
        libraryProductIds: Array.from(libraryProductIds),
        productsCreated,
        productsUpdated,
        itemsTracked,
        errors: errors.length > 0 ? errors : undefined,
      };

      logger.debug('Import completed:', {
        uniqueProducts: libraryProductIds.size,
        created: productsCreated,
        updated: productsUpdated,
        itemsTracked,
        errors: errors.length,
      });

      return result;
    } catch (error) {
      logger.error('Import service error:', error);
      throw error;
    }
  }

  /**
   * Group products by referenceId for deduplication
   * Products with the same referenceId will share ONE product_library entry
   */
  private groupProductsByReferenceId(
    products: PortalProduct[],
    selectionMap: Map<string, ProductSelection>,
  ): ProductGroup[] {
    const groups = new Map<string, ProductGroup>();

    for (const product of products) {
      const selection = selectionMap.get(product.id);
      if (!selection) continue;

      // Use referenceId as grouping key, fallback to portal item id
      const groupKey = product.referenceId || product.id;

      // Parse category and subcategory from selection
      let categoryValue: ProductCategory | null = null;
      let subcategoryValue: string | null = null;

      if (selection.category) {
        if (typeof selection.category === 'string') {
          // Legacy format - just a string
          categoryValue = selection.category as ProductCategory;
        } else if (typeof selection.category === 'object') {
          // New format - CategorySelection object with { category: string, subcategory?: string }
          const catSelection = selection.category as CategorySelection;
          categoryValue = catSelection.category as ProductCategory;
          subcategoryValue = catSelection.subcategory || null;
        }
      }

      // Also check for direct subcategory field (legacy)
      if (!subcategoryValue && selection.subcategory) {
        subcategoryValue = selection.subcategory;
      }

      if (!groups.has(groupKey)) {
        groups.set(groupKey, {
          referenceId: groupKey,
          products: [],
          category: categoryValue,
          subcategory: subcategoryValue,
        });
      }

      groups.get(groupKey)!.products.push(product);
    }

    return Array.from(groups.values());
  }

  /**
   * Create or update product in product_library
   * ONE entry per referenceId per organization
   */
  private async createOrUpdateLibraryProduct(
    product: PortalProduct,
    category: ProductCategory | null,
    subcategory?: string | null,
  ): Promise<{ id: string; created: boolean } | null> {
    try {
      const supabase = await this.getClient();
      // Check if product already exists by referenceId
      let existing = null;

      if (product.referenceId) {
        logger.debug('Checking for existing product with referenceId:', product.referenceId);

        const { data: existingByRef, error: checkError } = await supabase
          .from('product_library')
          .select('id')
          .eq('organization_id', this.organizationId)
          .eq('referenced_item_id', parseInt(product.referenceId, 10))
          .maybeSingle();

        if (checkError) {
          logger.error('Error checking for existing product:', {
            error: checkError,
            organizationId: this.organizationId,
            referenceId: product.referenceId,
          });
        }

        existing = existingByRef;
      }

      const productData: any = {
        organization_id: this.organizationId,
        category: category || 'other',
        subcategory: subcategory || null,
        brand: product.brand || 'Unknown Brand',
        model: product.model || product.name || 'Unknown Model',
        part_number: product.partNumber,
        description: product.description,
        short_description: product.name,
        price: product.sellPrice || product.unitPrice || 0,
        sell_price: product.sellPrice,
        cost: product.unitPrice,
        image_url: product.imageUrl || null, // Save Portal product image
        specifications: product.specifications || {},
        // Only set ONE identifier - preferring referenced_item_id when available
        // portal_item_id is only used as fallback when there's no referenceId
        referenced_item_id: product.referenceId ? parseInt(product.referenceId, 10) : null,
        portal_item_id: product.referenceId ? null : parseInt(product.id, 10),
        portal_metadata: {
          area: product.area,
          option: product.option,
          quantity: product.quantity,
          imageUrl: product.imageUrl, // Also store in metadata for reference
          // Store the portal_item_id here for reference
          portal_item_ids: [parseInt(product.id, 10)],
        },
        is_global: false,
        is_public: false,
        creation_method: 'manual', // Use 'manual' as Portal import is user-initiated (constraint allows: manual, ai_assisted, ai_verified, community)
        source: 'portal',
        updated_at: new Date().toISOString(),
        // Organization ownership (not user)
        ownership_type: 'organization',
        owner_id: this.organizationId,
        visibility_scope: 'org_only',
        portal_mapping_org_ids: [this.organizationId],
        // Note: created_by is UUID type, but we have Clerk ID (string)
        // We track the creator via organization_id and portal_metadata instead
      };

      // Enhanced debug logging for troubleshooting
      logger.debug('Product data to insert/update:', {
        referenced_item_id: productData.referenced_item_id,
        portal_item_id: productData.portal_item_id,
        organization_id: productData.organization_id,
        owner_id: productData.owner_id,
        ownership_type: productData.ownership_type,
        brand: productData.brand,
        model: productData.model,
        category: productData.category,
        existing: existing ? 'Found existing product' : 'No existing product found',
      });

      if (existing) {
        // Update existing product
        logger.debug(`Updating existing product for referenceId ${product.referenceId}`);

        const { data, error } = await supabase
          .from('product_library')
          .update(productData)
          .eq('id', existing.id)
          .select('id')
          .single();

        if (error) {
          logger.error('Database update error:', {
            error: error.message,
            code: error.code,
            details: error.details,
            hint: error.hint,
            productId: existing.id,
            organizationId: this.organizationId,
          });
          throw new Error(`Failed to update product: ${error.message || 'Database error'}`);
        }

        return { id: data.id, created: false };
      } else {
        // Create new product
        logger.debug(`Creating new product for referenceId ${product.referenceId || product.id}`);

        // Remove null portal_item_id to avoid constraint issues
        const insertData = {
          ...productData,
          created_at: new Date().toISOString(),
        };

        // If portal_item_id is null, remove it entirely from the insert
        if (insertData.portal_item_id === null) {
          delete insertData.portal_item_id;
        }

        logger.debug('Final insert data (portal_item_id removed if null):', {
          has_portal_item_id: 'portal_item_id' in insertData,
          portal_item_id_value: insertData.portal_item_id,
        });

        const { data, error } = await supabase
          .from('product_library')
          .insert(insertData)
          .select('id')
          .single();

        if (error) {
          logger.error('Database insert error:', {
            error: error.message,
            code: error.code,
            details: error.details,
            hint: error.hint,
            productData: {
              organization_id: insertData.organization_id,
              owner_id: insertData.owner_id,
              referenced_item_id: insertData.referenced_item_id,
              portal_item_id: insertData.portal_item_id,
              category: insertData.category,
            },
          });
          throw new Error(`Failed to create product: ${error.message || 'Database error'}`);
        }

        return { id: data.id, created: true };
      }
    } catch (error) {
      logger.error('Error creating/updating library product:', error);
      throw error;
    }
  }

  /**
   * Create tracking entry in portal_imported_products
   * Track EVERY Portal item for re-import and quantity tracking
   *
   * There are TWO unique constraints to handle:
   * 1. (clerk_org_id, portal_product_id) - same org can't import same portal product ID twice
   * 2. (clerk_org_id, reference_id, floor_plan_id) - same org can't import same reference to same floor plan
   */
  private async createImportedProductEntry(
    product: PortalProduct,
    libraryProductId: string,
    proposalId: number,
    proposalName: string,
    category: ProductCategory | null,
  ): Promise<void> {
    try {
      const supabase = await this.getClient();
      const referenceId = product.referenceId || product.id;

      const importData = {
        clerk_org_id: this.organizationId,
        floor_plan_id: this.floorPlanId || null,
        reference_id: referenceId,
        portal_product_id: product.id,
        portal_name: product.name,
        portal_description: product.description,
        portal_price: product.sellPrice || product.unitPrice || 0,
        portal_hierarchy: {
          area: product.area || 'General',
          option: product.option || 'Standard',
          item: product.name,
        },
        portal_data: {
          brand: product.brand,
          model: product.model,
          partNumber: product.partNumber,
          quantity: product.quantity,
          specifications: product.specifications,
        },
        category: category || 'other',
        manufacturer: product.brand,
        model: product.model,
        options: product.specifications || {},
        proposal_id: proposalId,
        proposal_name: proposalName,
        product_library_id: libraryProductId,
        imported_by: this.userId,
        imported_at: new Date().toISOString(),
      };

      // Use upsert to atomically handle race conditions
      // The unique_portal_product_per_floor_plan constraint is: (clerk_org_id, reference_id, floor_plan_id)
      // Using upsert prevents race conditions where multiple parallel imports check and then both try to insert
      logger.debug(
        `Upserting import record for portal_product_id: ${product.id}, reference_id: ${referenceId}`,
      );

      // First, check if there's an existing record for this product on THIS floor plan
      // This allows the same product to be imported to multiple floor plans
      let existingQuery = supabase
        .from('portal_imported_products')
        .select('id')
        .eq('clerk_org_id', this.organizationId)
        .eq('portal_product_id', product.id);

      // Add floor_plan_id filter - use .is() for null, .eq() otherwise
      if (this.floorPlanId) {
        existingQuery = existingQuery.eq('floor_plan_id', this.floorPlanId);
      } else {
        existingQuery = existingQuery.is('floor_plan_id', null);
      }

      const existingByPortalId = await existingQuery.maybeSingle();

      if (existingByPortalId.error && existingByPortalId.error.code !== 'PGRST116') {
        throw existingByPortalId.error;
      }

      // If found by portal_product_id + floor_plan_id, update that specific record
      if (existingByPortalId.data) {
        logger.debug(
          `Updating existing import record by portal_product_id: ${product.id} on floor_plan_id: ${this.floorPlanId}`,
        );
        // Exclude reference_id from update - it's part of unique constraint
        const { reference_id: _ref, ...updateDataWithoutKeys } = importData;
        const { error: updateError } = await supabase
          .from('portal_imported_products')
          .update(updateDataWithoutKeys as any)
          .eq('id', existingByPortalId.data.id);

        if (updateError) {
          logger.error('Error updating import tracking entry:', updateError);
          throw updateError;
        }
        return;
      }

      // Use upsert for new records - handles race conditions atomically
      // onConflict targets the unique_portal_product_per_floor_plan constraint
      // Exclude portal_product_id from updates to avoid violating the other constraint
      const { portal_product_id: _excludedPortalProductId, ...updateDataWithoutPortalId } =
        importData;

      const { error: upsertError } = await supabase
        .from('portal_imported_products')
        .upsert(importData as any, {
          onConflict: 'clerk_org_id,reference_id,floor_plan_id',
          ignoreDuplicates: false,
        });

      if (upsertError) {
        // If upsert fails due to portal_product_id constraint, try update without that field
        if (upsertError.code === '23505' && upsertError.message?.includes('portal_product_id')) {
          logger.debug('Upsert conflict on portal_product_id, retrying with update');
          const { error: updateError } = await supabase
            .from('portal_imported_products')
            .update(updateDataWithoutPortalId as any)
            .eq('clerk_org_id', this.organizationId)
            .eq('reference_id', referenceId)
            .eq('floor_plan_id', this.floorPlanId || '');

          if (updateError) {
            logger.error(
              'Error updating import tracking entry after upsert conflict:',
              updateError,
            );
            throw updateError;
          }
        } else {
          logger.error('Error upserting import tracking entry:', upsertError);
          throw upsertError;
        }
      }
    } catch (error) {
      logger.error('Error creating imported product entry:', error);
      throw error;
    }
  }

  /**
   * Get imported products for a proposal
   */
  async getImportedProductsByProposal(proposalId: string): Promise<any[]> {
    try {
      const supabase = await this.getClient();
      const { data, error } = await supabase
        .from('portal_imported_products')
        .select(
          `
          *,
          product_library (
            id,
            category,
            brand,
            model,
            price,
            description
          )
        `,
        )
        .eq('clerk_org_id', this.organizationId)
        .eq('proposal_id', proposalId)
        .order('imported_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      logger.error('Error fetching imported products:', error);
      return [];
    }
  }

  /**
   * Apply category to all products with matching reference ID
   * This ensures consistency across all products of the same type
   */
  async applyCategoryByReferenceId(referenceId: string, category: ProductCategory): Promise<void> {
    try {
      // Update product_library entries
      const refIdInt = /^\d+$/.test(referenceId) ? parseInt(referenceId, 10) : null;
      const supabase = await this.getClient();

      if (refIdInt) {
        const { error: libError } = await supabase
          .from('product_library')
          .update({ category })
          .eq('organization_id', this.organizationId)
          .eq('referenced_item_id', refIdInt);

        if (libError) throw libError;
      }

      // Update portal_imported_products entries
      const { error: portalError } = await supabase
        .from('portal_imported_products')
        .update({ category })
        .eq('clerk_org_id', this.organizationId)
        .eq('reference_id', referenceId);

      if (portalError) throw portalError;

      logger.debug(`Applied category ${category} to all products with referenceId ${referenceId}`);
    } catch (error) {
      logger.error('Error applying category by reference ID:', error);
      throw error;
    }
  }
}

export function createImportService(
  organizationId: string,
  floorPlanId?: string,
  userId?: string,
): PortalImportService {
  return new PortalImportService(organizationId, floorPlanId, userId);
}

// Export with the name expected by PortalImportDialog
export function createPortalImportService(): {
  fetchProposalForImport: (proposalId: number) => Promise<{ products: ImportableProduct[] }>;
  importProducts: (
    products: ImportableProduct[],
    floorPlanId: string,
    proposalId: number,
    categoryMappings: Map<string, { category: string; subcategory?: string }>,
  ) => Promise<{ success: boolean; imported: number; failed: number }>;
} {
  // This is a wrapper for the dialog's expected interface
  return {
    fetchProposalForImport: async (proposalId: number) => {
      // Mock implementation - you'll need to implement actual Portal.io API call
      // For now, return empty array to prevent errors
      return { products: [] };
    },
    importProducts: async (
      products: ImportableProduct[],
      floorPlanId: string,
      proposalId: number,
      categoryMappings: Map<string, { category: string; subcategory?: string }>,
    ) => {
      // Convert ImportableProduct[] to the format expected by PortalImportService
      const portalProducts: PortalProduct[] = products.map(p => ({
        id: p.portalItemId.toString(),
        name: p.name,
        brand: p.brand,
        model: p.model,
        description: p.description,
        partNumber: p.partNumber,
        quantity: p.quantity,
        unitPrice: p.unitPrice,
        sellPrice: p.sellPrice,
        totalPrice: p.sellPrice * p.quantity,
        referenceId: p.referenceId,
        area: p.areaName,
        option: p.optionName,
        imageUrl: p.imageUrl, // Pass through image URL
        specifications: p.specifications,
      }));

      // Convert category mappings to ProductSelection[]
      const selections: ProductSelection[] = products.map(p => {
        const mapping = categoryMappings.get(p.portalItemId.toString());
        return {
          productId: p.portalItemId.toString(),
          category: mapping ? { value: mapping.category, label: mapping.category } : null,
          subcategory: mapping?.subcategory || null,
          referenceId: p.referenceId,
        };
      });

      // Get organization ID from current user's context
      const context = await requireAuthenticatedContext();
      if (!context?.userId) {
        throw new Error('User not authenticated');
      }

      // You'll need to get the actual organization ID from your app's context
      const organizationId = context.organizationId || 'placeholder'; // Replace with actual org ID logic

      const service = new PortalImportService(organizationId, floorPlanId, context.userId);
      const result = await service.importProducts(
        portalProducts,
        selections,
        proposalId,
        `Portal Proposal #${proposalId}`,
      );

      return {
        success: result.success,
        imported: result.importedCount,
        failed: portalProducts.length - result.importedCount,
      };
    },
  };
}
